// src/domain/integration/Integration.ts

export const INTEGRATION_KEYS = ['sii', 'sat', 'dian', 'sunat', 'clara', 'tusdatos', 'invoicing_email', 'slack', 'erp', 'teams'] as const
export type IntegrationKey = typeof INTEGRATION_KEYS[number]

export function isIntegrationKey(key: string): key is IntegrationKey {
  return (INTEGRATION_KEYS as readonly string[]).includes(key)
}
export type IntegrationStatus = 'ok' | 'warning' | 'error' | 'pending' | 'disconnected'
export type IntegrationCategory = 'tax' | 'cards' | 'compliance' | 'reception' | 'notifications' | 'erp'
export type IntegrationAvailability = 'available' | 'coming_soon'
/** Every code the backend sends; each one has its `integrations.errors.<code>` sentence. */
export const INTEGRATION_ERROR_CODES = [
  'account_inactive',
  'all_recent_failed',
  'auth_failed',
  'cert_expired',
  'cert_expiring',
  'channel_not_found',
  'credentials',
  'credentials_rejected_unchanged',
  'failed_payments',
  'failed_reports',
  'forwarding_needs_code',
  'invalid_auth',
  'is_archived',
  'missing_scope',
  'missing_token',
  'no_recent_email',
  'not_authed',
  'not_in_channel',
  'person_missing',
  'provider_rejected',
  'provider_unavailable',
  'request_failed',
  'sii_test_throttled',
  'sync_failed',
  'token_revoked',
  'unknown',
] as const
export type IntegrationErrorCode = typeof INTEGRATION_ERROR_CODES[number]

export function isIntegrationErrorCode(code: string): code is IntegrationErrorCode {
  return (INTEGRATION_ERROR_CODES as readonly string[]).includes(code)
}
/** `message` is always "" from the backend: translate by `code`. */
export interface IntegrationError { code: string, message: string }

export type FormKey = 'sii-connect' | 'sii-certificate' | 'sat' | 'dian' | 'alanube' | 'clara' | 'tusdatos'

export type IntegrationAction = 'test' | 'connect' | 'disconnect' | 'save' | 'test_set'

export type SiiEntity = 'person' | 'company'
export interface SiiEntityStatus { rut: string | null, validated: boolean, lastVerifiedAt: string | null }

export interface SiiDetails {
  /** The clave row. */
  credentialId: number | null
  /** The row that holds the certificate; it can differ from the clave row. */
  certificateCredentialId: number | null
  companyConnected: boolean
  personConnected: boolean
  usesPublicInvoicingEntity: boolean | null
  /** Null when that entity has no stored password. */
  company: SiiEntityStatus | null
  person: SiiEntityStatus | null
}
export interface SatDetails { rfc: string | null, credentialId: number | null }
export interface DianDetails {
  credentialId: number | null
  receptionConnected: boolean
  alanubeRegistered: boolean
  testSetStatus: 'approved' | 'rejected' | 'pending' | null
}
export type ForwardingStatus = 'PENDING' | 'ACCEPTED' | 'FAILED'
export interface EmailForwarding {
  senderEmail: string
  status: ForwardingStatus
  acceptedAt: string | null
  /** Only ever set when the forwarding FAILED: Gmail's code is useless (and sensitive) otherwise. */
  confirmationCode: string | null
}
export interface EmailDetails {
  address: string | null
  received30d: number
  ok30d: number
  failed30d: number
  forwarding: EmailForwarding | null
}
export interface SlackDetails { lastOkAt: string | null, lastErrorCode: string | null }
export interface ErpDetails { erp: string | null, failedPayments30d: number }
export interface SunatDetails { credentialId: number | null }
/** The sibling whose credential a society without its own uses; null when it has its own or none. */
export interface SharedFrom { societyId: number, name: string }
export interface ClaraDetails { sharedFrom: SharedFrom | null }
export interface TusdatosDetails { failedReports30d: number, completedReports30d: number, sharedFrom: SharedFrom | null }

export interface SocietyHealth<D> {
  societyId: number
  name: string
  nationalId: string | null
  country: string | null
  status: IntegrationStatus
  lastSuccessAt: string | null
  lastAttemptAt: string | null
  error: IntegrationError | null
  certExpiresAt: string | null
  details: D
}

interface Base<K extends IntegrationKey> {
  key: K
  category: IntegrationCategory
  availability: IntegrationAvailability
  readOnly: boolean
  status: IntegrationStatus
  statusDetail: IntegrationError | null
}
export interface SocietyIntegration<K extends IntegrationKey, D> extends Base<K> {
  scope: 'society'
  societies: SocietyHealth<D>[]
}
export interface TenantIntegration<K extends IntegrationKey, D> extends Base<K> {
  scope: 'tenant'
  details: D
}

export type Integration =
  | SocietyIntegration<'sii', SiiDetails>
  | SocietyIntegration<'sat', SatDetails>
  | SocietyIntegration<'dian', DianDetails>
  | SocietyIntegration<'sunat', SunatDetails>
  | SocietyIntegration<'clara', ClaraDetails>
  | SocietyIntegration<'tusdatos', TusdatosDetails>
  | SocietyIntegration<'invoicing_email', EmailDetails>
  | TenantIntegration<'slack', SlackDetails | null>
  | TenantIntegration<'erp', ErpDetails | null>
  | TenantIntegration<'teams', null>

export interface TestResult { ok: boolean, errorCode: string | null }

export interface ClaraCredentialsDraft {
  societyId: number
  clientId: string
  clientSecret: string
  caCert: string
  clientCert: string
  clientKey: string
}

export interface TusdatosCredentialsDraft { societyId: number, username: string, password: string }

export interface SiiConnectInput {
  societyId: number
  entity: SiiEntity
  rut: string
  clave: string
  usesPublicInvoicingEntity: boolean | null
}
export interface SiiLogin { entity: SiiEntity, rut: string, clave: string }
/** Every login goes to the same endpoint; with no login at all only the invoicing entity changes. */
export interface SiiConnectPlan { societyId: number, usesPublicInvoicingEntity: boolean, logins: SiiLogin[] }

/** `credentialId` set → replaces that credential's files; null → creates the society's first one. */
export interface TaxCredentialUpload { credentialId: number | null, form: FormData }
export interface SiiCertificateDraft { societyId: number, credentialId: number | null, p12File: File, password: string }
export interface SatCredentialDraft { societyId: number, credentialId: number | null, cerFile: File, keyFile: File, password: string }

/** What the saved DIAN reception credential holds; `null` fields were never stored. */
export interface DianLegalRep { legalRepId: string | null, legalRepIdType: string | null }

export interface DianCredentialDraft { societyId: number, legalRepId: string, legalRepIdType: string, companyNit: string }

export interface AlanubeRegistrationDraft {
  societyId: number
  name: string
  tradeName: string
  identification: string
  dv: string
  organizationType: number
  identificationType: string
  regimeCodes: string[]
  taxCode: string
  economicActivities: string[]
  email: string
  phone: string
  address: { line: string, department: string, city: string }
}

export interface IntegrationRepository {
  list(): Promise<Integration[]>
  testSlack(): Promise<TestResult>
  testClara(societyId: number): Promise<TestResult>
  testSii(societyId: number): Promise<TestResult>
  /** SAT and DIAN only: CL/PE credentials have no sync provider, so the generic test always fails. */
  testTaxCredential(credentialId: number): Promise<TestResult>
  connectSlackUrl(): Promise<string>
  disconnectSlack(): Promise<void>
  saveClara(draft: ClaraCredentialsDraft): Promise<void>
  disconnectClara(societyId: number): Promise<void>
  saveTusdatos(draft: TusdatosCredentialsDraft): Promise<void>
  disconnectTusdatos(societyId: number): Promise<void>
  /** SAT and DIAN reception only: SII and RADIAN have their own disconnect. */
  disconnectTaxCredential(credentialId: number): Promise<void>
  disconnectSii(societyId: number, entity: SiiEntity): Promise<void>
  /** The Finnecto address the society forwards its DIAN access emails to; null while none is assigned. */
  dianForwardingInstructions(societyId: number): Promise<string | null>
  /** `governmentId` is the TestSetId DIAN hands out; production rejects the request without it. */
  alanubeTestSet(societyId: number, governmentId: string): Promise<void>
  /** `validated: false` means saved but unchecked: the SII did not answer. */
  connectSii(input: SiiConnectInput): Promise<{ validated: boolean }>
  setSiiInvoicingEntity(societyId: number, usesPublicInvoicingEntity: boolean): Promise<void>
  saveTaxCredential(input: TaxCredentialUpload): Promise<void>
  saveDianCredential(draft: DianCredentialDraft): Promise<void>
  dianLegalRep(credentialId: number): Promise<DianLegalRep>
  registerAlanube(draft: AlanubeRegistrationDraft): Promise<void>
}
